import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Container,
    Stack,
    Alert,
    CircularProgress,
    Fade,
    Snackbar,
    Divider,
    useTheme,
    useMediaQuery,
    Zoom,
    Chip,
    Grid
} from "@mui/material";
import VideoIcon from "@mui/icons-material/OndemandVideo";
import SummarizeIcon from "@mui/icons-material/Summarize";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GenerateIcon from "@mui/icons-material/AutoFixHigh";
import LinkIcon from "@mui/icons-material/Link";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import api from "../axios/axiosInstance";
import AIGenerationLimitDialog from "../dialogs/AIGenerationLimitDialog";
import handleAIError from "../utils/handleAIError";
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from "docx";
import { SpinningGradelyLoader, FloatingGradelyLoader } from '../loadingIcons/LoadingIcons';



const YouTubeSummaryGenerator = ({ setActiveTabGlobal }) => {
    const [youtubeUrl, setYoutubeUrl] = useState("");
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [showLimitDialog, setShowLimitDialog] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const generateWordDocument = async (summary, videoTitle, duration) => {
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: [
                        // Title
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "EDUCATIONAL VIDEO SUMMARY",
                                    bold: true,
                                    size: 32,
                                    color: "2E7D32"
                                })
                            ],
                            heading: HeadingLevel.TITLE,
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 300 }
                        }),

                        // Video Information
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Video: ",
                                    bold: true,
                                    size: 24
                                }),
                                new TextRun({
                                    text: videoTitle,
                                    size: 24
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 200 }
                        }),

                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: `Duration: ${duration} minutes`,
                                    size: 20,
                                    italics: true
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { after: 400 }
                        }),

                        // Overview Section
                        ...(summary.overview ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "📚 OVERVIEW",
                                        bold: true,
                                        size: 28,
                                        color: "1976D2"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: summary.overview,
                                        size: 22
                                    })
                                ],
                                spacing: { after: 300 }
                            })
                        ] : []),

                        // Key Learning Points
                        ...(summary.key_learning_points && summary.key_learning_points.length > 0 ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "💡 KEY LEARNING POINTS",
                                        bold: true,
                                        size: 28,
                                        color: "2E7D32"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            ...summary.key_learning_points.map((point, index) =>
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${index + 1}. `,
                                            bold: true,
                                            size: 22,
                                            color: "2E7D32"
                                        }),
                                        new TextRun({
                                            text: point,
                                            size: 22
                                        })
                                    ],
                                    spacing: { after: 100 }
                                })
                            )
                        ] : []),

                        // Important Details
                        ...(summary.important_details && summary.important_details.length > 0 ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "✅ IMPORTANT DETAILS",
                                        bold: true,
                                        size: 28,
                                        color: "0288D1"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            ...summary.important_details.map(detail =>
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: "• ",
                                            bold: true,
                                            size: 22,
                                            color: "0288D1"
                                        }),
                                        new TextRun({
                                            text: detail,
                                            size: 22
                                        })
                                    ],
                                    spacing: { after: 100 }
                                })
                            )
                        ] : []),

                        // Discussion Questions
                        ...(summary.discussion_questions && summary.discussion_questions.length > 0 ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "❓ DISCUSSION QUESTIONS",
                                        bold: true,
                                        size: 28,
                                        color: "ED6C02"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            ...summary.discussion_questions.map((question, index) =>
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${index + 1}. `,
                                            bold: true,
                                            size: 22,
                                            color: "ED6C02"
                                        }),
                                        new TextRun({
                                            text: question,
                                            size: 22,
                                            italics: true
                                        })
                                    ],
                                    spacing: { after: 100 }
                                })
                            )
                        ] : []),

                        // Practical Applications
                        ...(summary.practical_applications && summary.practical_applications.length > 0 ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "🔗 PRACTICAL APPLICATIONS",
                                        bold: true,
                                        size: 28,
                                        color: "7B1FA2"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            ...summary.practical_applications.map(application =>
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: "• ",
                                            bold: true,
                                            size: 22,
                                            color: "7B1FA2"
                                        }),
                                        new TextRun({
                                            text: application,
                                            size: 22
                                        })
                                    ],
                                    spacing: { after: 100 }
                                })
                            )
                        ] : []),

                        // Vocabulary Terms
                        ...(summary.vocabulary_terms && summary.vocabulary_terms.length > 0 ? [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: "📖 VOCABULARY & KEY TERMS",
                                        bold: true,
                                        size: 28,
                                        color: "D32F2F"
                                    })
                                ],
                                heading: HeadingLevel.HEADING_1,
                                spacing: { before: 300, after: 200 }
                            }),
                            ...summary.vocabulary_terms.map(vocab =>
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: `${vocab.term}: `,
                                            bold: true,
                                            size: 22,
                                            color: "D32F2F"
                                        }),
                                        new TextRun({
                                            text: vocab.definition,
                                            size: 22
                                        })
                                    ],
                                    spacing: { after: 150 }
                                })
                            )
                        ] : []),

                        // Footer
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: "Generated by Gradely Educational Platform",
                                    size: 18,
                                    italics: true,
                                    color: "757575"
                                })
                            ],
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 600 }
                        })
                    ]
                }
            ]
        });

        return doc;
    };

    const downloadWordDocument = async (summary, videoTitle, duration) => {
        try {
            const doc = await generateWordDocument(summary, videoTitle, duration);
            const blob = await Packer.toBlob(doc);

            const element = document.createElement('a');
            element.href = URL.createObjectURL(blob);
            element.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_educational_summary.docx`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            URL.revokeObjectURL(element.href);
        } catch (error) {
            // Error handled silently - fallback to text download
            const summaryText = formatSummaryForDownload(summary);
            const element = document.createElement('a');
            const file = new Blob([summaryText], { type: 'text/plain' });
            element.href = URL.createObjectURL(file);
            element.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary.txt`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }
    };

    const formatSummaryForDownload = (summary) => {
        let text = `EDUCATIONAL SUMMARY\n`;
        text += `===================\n\n`;

        if (summary.overview) {
            text += `OVERVIEW:\n${summary.overview}\n\n`;
        }

        if (summary.key_learning_points && summary.key_learning_points.length > 0) {
            text += `KEY LEARNING POINTS:\n`;
            summary.key_learning_points.forEach((point, index) => {
                text += `${index + 1}. ${point}\n`;
            });
            text += `\n`;
        }

        if (summary.important_details && summary.important_details.length > 0) {
            text += `IMPORTANT DETAILS:\n`;
            summary.important_details.forEach((detail, index) => {
                text += `• ${detail}\n`;
            });
            text += `\n`;
        }

        if (summary.discussion_questions && summary.discussion_questions.length > 0) {
            text += `DISCUSSION QUESTIONS:\n`;
            summary.discussion_questions.forEach((question, index) => {
                text += `${index + 1}. ${question}\n`;
            });
            text += `\n`;
        }

        if (summary.practical_applications && summary.practical_applications.length > 0) {
            text += `PRACTICAL APPLICATIONS:\n`;
            summary.practical_applications.forEach((app, index) => {
                text += `• ${app}\n`;
            });
            text += `\n`;
        }

        if (summary.vocabulary_terms && summary.vocabulary_terms.length > 0) {
            text += `VOCABULARY & KEY TERMS:\n`;
            summary.vocabulary_terms.forEach((vocab) => {
                text += `${vocab.term}: ${vocab.definition}\n`;
            });
            text += `\n`;
        }

        return text;
    };

    const handleSubmit = async () => {
        if (!youtubeUrl) {
            setError("Please enter a YouTube URL.");
            return;
        }

        setLoading(true);
        setError("");
        setResponse(null);

        try {
            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/youtube-summary/`,
                { youtube_url: youtubeUrl },
                { withCredentials: true }
            );
            setResponse(res.data);
        } catch (err) {
            handleAIError(err, {
                setErrorMsg: (msg) => setError(msg),
                setShowLimitDialog,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
            <Fade in timeout={800}>
                <Box>
                    <Box sx={{ textAlign: 'center', mb: 2, mt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 0.5 }}>

                            <Typography sx={{ fontSize: '2rem', fontWeight: 600, color: theme.palette.primary.main }}>
                                <PlayCircleOutlineIcon sx={{ fontSize: "inherit" }} /> YouTube Educational Summarizer
                            </Typography>
                        </Box>
                    </Box>

                    <Typography sx={{ fontSize: '1rem', fontStyle: 'italic', textAlign: 'center', mb: 4, color: theme.palette.text.secondary, lineHeight: 1.6, maxWidth: '800px', mx: 'auto' }}>
                        Convert YouTube videos into structured educational content. Paste a video link to generate lesson-ready summaries and activities aligned with your teaching needs.


                    </Typography>


                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    <Paper
                        elevation={3}
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: { xs: 1.5, sm: 2, md: 3 },
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 4,
                        }}
                    >
                        <Stack spacing={3}>
                            <TextField
                                label="YouTube Video URL"
                                fullWidth
                                value={youtubeUrl}
                                onChange={(e) => setYoutubeUrl(e.target.value)}
                                placeholder="https://www.youtube.com/watch?v=..."
                                InputProps={{
                                    startAdornment: <LinkIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                size="large"
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontSize: "1.1rem",
                                    fontWeight: "bold",
                                    backgroundColor: theme.palette.primary.main,
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary.dark,
                                        transform: "translateY(-2px)",
                                        boxShadow: 4,
                                    },
                                }}
                            >
                                {loading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Generating Educational Summary...</span>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <GenerateIcon />
                                        <span>Generate Educational Summary</span>
                                    </Stack>
                                )}
                            </Button>
                        </Stack>
                    </Paper>

                    {response && (
                        <Zoom in timeout={600}>
                            <Paper
                                elevation={4}
                                sx={{
                                    p: { xs: 2, sm: 3, md: 4 },
                                    borderRadius: { xs: 1.5, sm: 2, md: 3 },
                                    background: theme.palette.background.default,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {/* Video Header Information */}
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant={isSmallMobile ? "h6" : "h5"}
                                        fontWeight="bold"
                                        gutterBottom
                                        sx={{
                                            color: theme.palette.primary.main,
                                            mb: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            textAlign: 'center'
                                        }}
                                    >
                                        <PlayCircleOutlineIcon sx={{ fontSize: 'inherit' }} />
                                        {response.title}
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                elevation={1}
                                                sx={{
                                                    p: 2,
                                                    borderLeft: `4px solid ${theme.palette.info.main}`,
                                                    backgroundColor: theme.palette.grey[50],
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        fontWeight: 'medium'
                                                    }}
                                                >
                                                    <AccessTimeIcon fontSize="small" color="info" />
                                                    <strong>Duration:</strong> {response.duration_minutes} minutes
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                elevation={1}
                                                sx={{
                                                    p: 2,
                                                    borderLeft: `4px solid ${theme.palette.success.main}`,
                                                    backgroundColor: theme.palette.grey[50],
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        fontWeight: 'medium'
                                                    }}
                                                >
                                                    <MenuBookIcon fontSize="small" color="success" />
                                                    <strong>Source:</strong> {response.used_captions
                                                        ? "YouTube Captions"
                                                        : "AI Transcription"}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Chip
                                        icon={<SchoolIcon />}
                                        label="Educational Content Ready for Classroom Use"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                            fontSize: '0.875rem',
                                            fontWeight: 'medium',
                                            mb: 3
                                        }}
                                    />
                                </Box>

                                <Divider
                                    sx={{
                                        mb: 3,
                                        height: 3,
                                        borderRadius: 2,
                                        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                    }}
                                />

                                {/* Educational Summary Content */}
                                <Stack spacing={3}>
                                    {/* Download Header */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <SummarizeIcon fontSize="small" />
                                            Educational Summary
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => {
                                                downloadWordDocument(response.summary, response.title, response.duration_minutes);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                backgroundColor: theme.palette.primary.main,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.primary.dark,
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: 4
                                                }
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <DownloadIcon />
                                                <span>Download Word</span>
                                            </Stack>
                                        </Button>
                                    </Box>

                                    {/* Overview Section */}
                                    {response.summary.overview && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <MenuBookIcon fontSize="small" />
                                                Overview
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    lineHeight: 1.8,
                                                    fontSize: isSmallMobile ? '0.875rem' : '1rem',
                                                    color: theme.palette.text.primary
                                                }}
                                            >
                                                {response.summary.overview}
                                            </Typography>
                                        </Paper>
                                    )}

                                    {/* Key Learning Points */}
                                    {response.summary.key_learning_points && response.summary.key_learning_points.length > 0 && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.success.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.success.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <EmojiObjectsIcon fontSize="small" />
                                                Key Learning Points
                                            </Typography>
                                            <List dense disablePadding>
                                                {response.summary.key_learning_points.map((point, index) => (
                                                    <ListItem key={index} sx={{ pl: 0, mb: 1 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <CheckCircleOutlineIcon
                                                                color="success"
                                                                fontSize="small"
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        lineHeight: 1.6,
                                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                                                    }}
                                                                >
                                                                    {point}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}

                                    {/* Important Details */}
                                    {response.summary.important_details && response.summary.important_details.length > 0 && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.info.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.info.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <FactCheckIcon fontSize="small" />
                                                Important Details
                                            </Typography>
                                            <List dense disablePadding>
                                                {response.summary.important_details.map((detail, index) => (
                                                    <ListItem key={index} sx={{ pl: 0, mb: 1 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <CheckCircleOutlineIcon
                                                                color="info"
                                                                fontSize="small"
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        lineHeight: 1.6,
                                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                                                    }}
                                                                >
                                                                    {detail}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}

                                    {/* Discussion Questions */}
                                    {response.summary.discussion_questions && response.summary.discussion_questions.length > 0 && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.warning.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.warning.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <QuestionAnswerIcon fontSize="small" />
                                                Discussion Questions
                                            </Typography>
                                            <List dense disablePadding>
                                                {response.summary.discussion_questions.map((question, index) => (
                                                    <ListItem key={index} sx={{ pl: 0, mb: 1 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    color: theme.palette.warning.main,
                                                                    fontWeight: 'bold',
                                                                    fontSize: '1rem'
                                                                }}
                                                            >
                                                                {index + 1}.
                                                            </Typography>
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        lineHeight: 1.6,
                                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    {question}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}

                                    {/* Practical Applications */}
                                    {response.summary.practical_applications && response.summary.practical_applications.length > 0 && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.secondary.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.secondary.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <TipsAndUpdatesIcon fontSize="small" />
                                                Practical Applications
                                            </Typography>
                                            <List dense disablePadding>
                                                {response.summary.practical_applications.map((application, index) => (
                                                    <ListItem key={index} sx={{ pl: 0, mb: 1 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                                            <CheckCircleOutlineIcon
                                                                color="secondary"
                                                                fontSize="small"
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        lineHeight: 1.6,
                                                                        fontSize: isSmallMobile ? '0.875rem' : '1rem'
                                                                    }}
                                                                >
                                                                    {application}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}

                                    {/* Vocabulary Terms */}
                                    {response.summary.vocabulary_terms && response.summary.vocabulary_terms.length > 0 && (
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                p: { xs: 2, sm: 3 },
                                                borderRadius: 3,
                                                backgroundColor: theme.palette.background.paper,
                                                borderLeft: `4px solid ${theme.palette.error.main}`,
                                                transition: 'all 0.3s ease',
                                                '&:hover': { boxShadow: 4 }
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                gutterBottom
                                                sx={{
                                                    color: theme.palette.error.main,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <BookmarksIcon fontSize="small" />
                                                Vocabulary & Key Terms
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {response.summary.vocabulary_terms.map((vocab, index) => (
                                                    <Grid item xs={12} sm={6} key={index}>
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                p: 2,
                                                                borderRadius: 2,
                                                                backgroundColor: theme.palette.grey[50],
                                                                border: `1px solid ${theme.palette.error.light}`,
                                                                transition: 'all 0.3s ease',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: 2
                                                                }
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="subtitle2"
                                                                fontWeight="bold"
                                                                sx={{
                                                                    color: theme.palette.error.main,
                                                                    mb: 1
                                                                }}
                                                            >
                                                                {vocab.term}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    lineHeight: 1.6,
                                                                    fontSize: isSmallMobile ? '0.75rem' : '0.875rem'
                                                                }}
                                                            >
                                                                {vocab.definition}
                                                            </Typography>
                                                        </Paper>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Paper>
                                    )}
                                </Stack>
                            </Paper>
                        </Zoom>
                    )}

                    {/* Loading State */}
                    {loading && (
                        <Box
                            sx={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                backgroundColor: "rgba(255,255,255,0.9)",
                                backdropFilter: "blur(8px)",
                                zIndex: 2000,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Paper
                                elevation={8}
                                sx={{
                                    p: { xs: 3, sm: 4 },
                                    borderRadius: 4,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    backgroundColor: "white",
                                    maxWidth: 400,
                                    mx: 2
                                }}
                            >
                                <FloatingGradelyLoader
                                    size={60}
                                    showText={true}
                                    text="Extracting transcript and generating educational summary..."
                                />
                            </Paper>
                        </Box>
                    )}

                    <Snackbar
                        open={!!error}
                        autoHideDuration={6000}
                        onClose={() => setError("")}
                    >
                        <Alert severity="error" onClose={() => setError("")}>
                            {error}
                        </Alert>
                    </Snackbar>
                </Box>
            </Fade>
            <AIGenerationLimitDialog
                open={showLimitDialog}
                onClose={() => setShowLimitDialog(false)}
                featureName="YouTube Educational Summary"
                goToUpgrade={() => setActiveTabGlobal?.("Upgrade")}
            />
        </Container>
    );
};

export default YouTubeSummaryGenerator;
